/**
 * useMessages — minimal web port. RN's hook is ~700 lines and does double
 * duty as a Socket.IO consumer and a chat list/sender. This web version
 * sticks to the core surface used by the companion feed:
 *   - `messages` — merged voice + text history (ASC) from
 *     `/functions/v1/chat?sessionId=`
 *   - `loading`, `error`
 *   - `sendText(text)` → POST `/chat/v1/{sessionId}/message`, then refresh
 *   - polled refresh every 5s while a session is selected
 *
 * Real-time delta is light-touch — for richer Socket.IO integration we'll
 * fold this into a SocketContext consumer in a follow-up.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getMergedHistory,
  sendChatMessage,
  type ChatHistoryItem,
} from '../api/chatApi';
import { useLanguage } from '../hooks/useLanguage';
import logger from '../utils/logger';

export interface UseMessagesReturn {
  messages: ChatHistoryItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  sendText: (text: string) => Promise<void>;
}

export function useMessages(
  sessionId: string | null | undefined,
  options: { pollIntervalMs?: number; enabled?: boolean } = {},
): UseMessagesReturn {
  const { pollIntervalMs = 5000, enabled = true } = options;
  const { selectedLanguage } = useLanguage();

  const [messages, setMessages] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) {
      setMessages([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getMergedHistory(sid);
      setMessages(result.messages);
    } catch (err: any) {
      const msg = err?.message || 'Failed to load messages';
      setError(msg);
      logger.error('[useMessages] Failed to fetch', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const sendText = useCallback(
    async (text: string) => {
      const sid = sessionIdRef.current;
      if (!sid || !text.trim()) return;
      try {
        await sendChatMessage(sid, text, { language: selectedLanguage });
        await refresh();
      } catch (err: any) {
        const msg = err?.message || 'Failed to send message';
        setError(msg);
        logger.error('[useMessages] Send failed', err);
      }
    },
    [selectedLanguage, refresh],
  );

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    if (!enabled) return;
    refresh();
    if (pollIntervalMs > 0) {
      pollRef.current = setInterval(refresh, pollIntervalMs);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [sessionId, pollIntervalMs, refresh, enabled]);

  return { messages, loading, error, refresh, sendText };
}

export default useMessages;
